/**
 * Shared Types for Request Detail Stage Views
 *
 * This file contains all the type definitions and interfaces used across
 * the different stage view components (Create, Interview, Develop, Review, Finalize, Closed).
 */

import type {
  Project,
  Request,
  RequestStatus as DbRequestStatus,
} from '@cortex/database';
import { CheckCircle, Code, Eye, FileText, MessageSquare } from 'lucide-react';

// =============================================================================
// Core Component Props
// =============================================================================

/** Base props for all stage view components */
export interface StageViewProps {
  request: Request;
  onRequestUpdate?: (request: Request) => void;
}

// =============================================================================
// Stage Configuration
// =============================================================================

/** Stage configuration for display */
interface StageConfig {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  textClass: string;
  bgClass: string;
  borderClass: string;
}

/** Stage configuration map */
export const STAGE_CONFIG: Record<DbRequestStatus, StageConfig> = {
  create: {
    label: 'Created',
    description: 'Request has been created',
    icon: FileText,
    textClass: 'text-foreground/60',
    bgClass: 'bg-foreground/10',
    borderClass: 'border-foreground/40',
  },
  interview: {
    label: 'Interview',
    description: 'Gathering requirements through conversation',
    icon: MessageSquare,
    textClass: 'text-primary',
    bgClass: 'bg-primary/10',
    borderClass: 'border-primary',
  },
  develop: {
    label: 'Development',
    description: 'AI agent is implementing the solution',
    icon: Code,
    textClass: 'text-warning',
    bgClass: 'bg-warning/10',
    borderClass: 'border-warning',
  },
  review: {
    label: 'Review',
    description: 'Preview is ready for review',
    icon: Eye,
    textClass: 'text-info',
    bgClass: 'bg-info/10',
    borderClass: 'border-info',
  },
  finalize: {
    label: 'Finalize',
    description: 'Creating pull request',
    icon: CheckCircle,
    textClass: 'text-success',
    bgClass: 'bg-success/10',
    borderClass: 'border-success',
  },
  closed: {
    label: 'Closed',
    description: 'Request has been completed',
    icon: CheckCircle,
    textClass: 'text-success',
    bgClass: 'bg-success/10',
    borderClass: 'border-success',
  },
};

/** Workflow stages in order */
export const WORKFLOW_STAGES: DbRequestStatus[] = [
  'create',
  'interview',
  'develop',
  'review',
  'finalize',
  'closed',
];

// =============================================================================
// Review Stage Types
// =============================================================================

/** Maximum number of revisions allowed per request */
export const MAX_REVISIONS = 5;

/** Preview status response type */
export interface PreviewStatus {
  requestId: string;
  status:
    | 'not_started'
    | 'pending'
    | 'building'
    | 'ready'
    | 'failed'
    | 'cancelled';
  branchName: string | null;
  previewUrl: string | null;
  deploymentId?: string;
  error?: {
    code: string;
    message: string;
    suggestion?: string;
  };
  buildDurationMs?: number;
  createdAt?: string;
  readyAt?: string;
}

// =============================================================================
// Finalize Stage Types
// =============================================================================

// PR state is defined locally in FinalizeStageView

// =============================================================================
// Extended Types
// =============================================================================

/** Extended props types for specific stage views */
export interface FinalizeStageViewProps extends StageViewProps {
  // Project and onRequestUpdate kept for potential future use
  // but not currently used since closing happens via GitHub webhook
  project?: Project | null;
  onRequestUpdate?: (request: Request) => void;
}
